import React, { useState, useEffect, useRef } from "react";
import { Edit2 } from "lucide-react";
import { AuthContainer } from "./AuthContainer";
import { VerificationCode } from "./VerificationCode";
import { useAuth } from "./AuthContext";
import { apiPost } from "../services/api.service";
import { showError } from "../services/notification.service";
import { useTranslation } from "../i18n/TranslationContext";

export function PhoneVerification() {
	const [code, setCode] = useState("");
	const [countdown, setCountdown] = useState(60); // Start at 60 seconds
	const [canResend, setCanResend] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");
	const [tokenExpired, setTokenExpired] = useState(false);
	const [lastSentTime, setLastSentTime] = useState<number>(Date.now());
	const [isCheckingToken, setIsCheckingToken] = useState(true);
	const { setCurrentStep, authData } = useAuth();
	const { t } = useTranslation();
	const hasSentRef = useRef(false);

	// Check token immediately before any other logic
	useEffect(() => {
		const emailVerificationToken = sessionStorage.getItem(
			"emailVerificationToken"
		);
		if (!emailVerificationToken) {
			setTokenExpired(true);
		}
		setIsCheckingToken(false);
	}, []);

	// Set countdown based on last sent time (SMS already sent from PhoneNumber component)
	useEffect(() => {
		// Skip if we're still checking token or if token is expired
		if (isCheckingToken || tokenExpired) return;
		if (authData.phone && !hasSentRef.current) {
			hasSentRef.current = true; // Mark as handled

			// Check if we have a recent send time stored
			const storedLastSent = sessionStorage.getItem(
				`sms_last_sent_${authData.phone}`
			);
			if (storedLastSent) {
				const timeSinceLast = Date.now() - parseInt(storedLastSent);
				const remainingMs = 60000 - timeSinceLast; // 60 seconds

				if (remainingMs > 0) {
					// SMS was recently sent, just set the countdown
					const remainingSeconds = Math.ceil(remainingMs / 1000);
					setCountdown(remainingSeconds);
					setCanResend(false);
				} else {
					// Cooldown expired
					setCountdown(0);
					setCanResend(true);
				}
			} else {
				// No stored time, SMS should have been sent from PhoneNumber component
				// Start countdown from 60 seconds
				setCountdown(60);
				setCanResend(false);
			}
		}
	}, [isCheckingToken, tokenExpired, authData.phone]);

	useEffect(() => {
		if (countdown > 0) {
			const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
			return () => clearTimeout(timer);
		} else {
			setCanResend(true);
		}
	}, [countdown]);

	const sendSMSVerification = async () => {
		try {
			setIsLoading(true);
			setError("");

			// Get the email verification token from sessionStorage
			const emailVerificationToken = sessionStorage.getItem(
				"emailVerificationToken"
			);

			if (!emailVerificationToken) {
				setTokenExpired(true);
				setError(""); // Clear error since we'll show custom UI
				return;
			}

			const response = await apiPost<any>(
				"/verification/sms/send",
				{
					phoneNumber: authData.phone?.trim(),
					emailVerificationToken,
				},
				{ notification: { showError: true } }
			);

			if (response.success) {
				// Store the send time for rate limiting
				sessionStorage.setItem(
					`sms_last_sent_${authData.phone}`,
					Date.now().toString()
				);
				// Reset countdown on successful send
				setCountdown(60);
				setCanResend(false);
				setLastSentTime(Date.now());
			} else if (response.data?.remainingSeconds) {
				// Handle rate limit error - don't show duplicate error in UI
				setCountdown(response.data.remainingSeconds);
				setCanResend(false);
				// The notification service will show the error message
			} else {
				// Check if it's a token expiration error - check for ANY token-related error message
				// Check both response.message, response.error, and response.data?.message
				const errorMessage =
					response.message || response.error || response.data?.message || "";
				if (
					errorMessage &&
					(errorMessage.toLowerCase().includes("expired") ||
						errorMessage.toLowerCase().includes("email verification") ||
						errorMessage.toLowerCase().includes("token"))
				) {
					setTokenExpired(true);
					setError(""); // Clear error since we'll show custom UI
					// Also clear the invalid token from sessionStorage
					sessionStorage.removeItem("emailVerificationToken");
				} else {
					setError(errorMessage || t("phoneVerification.failedToSend"));
				}
			}
		} catch (err: any) {
			// Check if it's a rate limit error (429)
			if (err?.status === 429 && err?.data?.remainingSeconds) {
				// Don't show error in UI for rate limit - notification is enough
				setCountdown(err.data.remainingSeconds);
				setCanResend(false);
			} else {
				// Check if it's a token expiration/verification required error
				const errorMessage = err?.message || err?.data?.message || "";
				if (
					errorMessage &&
					(errorMessage.toLowerCase().includes("expired") ||
						errorMessage
							.toLowerCase()
							.includes("email verification required") ||
						errorMessage.toLowerCase().includes("verify your email"))
				) {
					setTokenExpired(true);
					setError(""); // Clear error since we'll show custom UI
					// Also clear the invalid token from sessionStorage
					sessionStorage.removeItem("emailVerificationToken");
				} else {
					setError(
						errorMessage || "Failed to send SMS code. Please try again."
					);
				}
			}
			console.error("SMS verification send error:", err);
		} finally {
			setIsLoading(false);
		}
	};

	const handleCodeComplete = async (verificationCode: string) => {
		setCode(verificationCode);
		setIsLoading(true);
		setError("");

		try {
			const emailVerificationToken = sessionStorage.getItem(
				"emailVerificationToken"
			);

			const response = await apiPost("/verification/sms/verify", {
				phoneNumber: authData.phone?.trim(),
				code: verificationCode,
				emailVerificationToken,
			});

			if (response.success) {
				// Keep the verification token for password creation
				// It will be cleared by the backend after password is set
				setCurrentStep(4); // Go to password creation
			} else {
				// Check if it's a token expiration error
				const errorMessage = response.message || response.data?.message || "";
				if (
					response.error === "TOKEN_EXPIRED" ||
					(errorMessage &&
						(errorMessage.toLowerCase().includes("expired") ||
							errorMessage.toLowerCase().includes("email verification") ||
							errorMessage.toLowerCase().includes("invalid or expired") ||
							errorMessage.toLowerCase().includes("invalid session") ||
							errorMessage.toLowerCase().includes("verify your email")))
				) {
					setTokenExpired(true);
					setError(""); // Clear error since we'll show custom UI
					sessionStorage.removeItem("emailVerificationToken");
				} else {
					setError(errorMessage || t("phoneVerification.invalidCode"));
				}
			}
		} catch (err: any) {
			// Check if it's a token expiration error in catch block too
			const errorMessage = err?.message || err?.data?.message || "";
			if (
				errorMessage &&
				(errorMessage.toLowerCase().includes("expired") ||
					errorMessage.toLowerCase().includes("email verification") ||
					errorMessage.toLowerCase().includes("invalid or expired") ||
					errorMessage.toLowerCase().includes("invalid session") ||
					errorMessage.toLowerCase().includes("verify your email"))
			) {
				setTokenExpired(true);
				setError(""); // Clear error since we'll show custom UI
				sessionStorage.removeItem("emailVerificationToken");
			} else {
				setError(t("phoneVerification.failedToVerify"));
			}
			console.error("SMS verification error:", err);
		} finally {
			setIsLoading(false);
		}
	};

	const handleResend = async () => {
		setCode("");
		setError("");
		hasSentRef.current = true; // Prevent double sending on resend too
		await sendSMSVerification();
	};

	const handleEditPhone = () => {
		setCurrentStep(2); // Go back to phone number input
	};

	const handleBack = () => {
		setCurrentStep(2);
	};

	const handleVerifyEmail = () => {
		// Clear the expired token from session
		sessionStorage.removeItem("emailVerificationToken");
		// Set a flag to auto-send email on navigation
		sessionStorage.setItem("autoSendEmail", "true");
		// Navigate to email verification step
		setCurrentStep(1);
	};

	// Show nothing while checking token to prevent flash
	if (isCheckingToken) {
		return null;
	}

	// If token is expired, show special UI
	if (tokenExpired) {
		return (
			<AuthContainer showBack={false} showProgress>
				<div className="text-center">
					<h1 className="text-[28px] font-bold text-[var(--ucm-text-dark)] mb-4 leading-tight">
						{t("phoneVerification.emailVerificationExpired")}
					</h1>
					<p className="text-[var(--ucm-text-medium)] mb-8">
						{t("phoneVerification.pleaseVerifyAgain")}
					</p>
					<button
						onClick={handleVerifyEmail}
						className="w-full h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] disabled:bg-[var(--ucm-text-light)] disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2"
					>
						{t("phoneVerification.verifyEmail")}
					</button>
				</div>
			</AuthContainer>
		);
	}

	return (
		<AuthContainer showBack showProgress onBack={handleBack}>
			<div className="text-center mb-8">
				<h1 className="text-[28px] font-bold text-[var(--ucm-text-dark)] mb-8 leading-tight">
					{t("phoneVerification.title")}
				</h1>
				<div className="flex items-center justify-center space-x-2 mb-8">
					<p className="text-[var(--ucm-text-medium)]">
						{t("phoneVerification.subtitle", { phone: authData.phone })}
					</p>
					<button
						onClick={handleEditPhone}
						className="text-[var(--ucm-blue-link)] hover:text-[var(--ucm-blue-link-hover)] p-1"
					>
						<Edit2 size={16} />
					</button>
				</div>
			</div>

			<div className="mb-8">
				<VerificationCode
					length={6}
					onComplete={handleCodeComplete}
					onCodeChange={setCode}
					value={code}
				/>
				{error && (
					<p className="text-sm text-red-500 mt-2 text-center">{error}</p>
				)}
			</div>

			<div className="text-center mb-8">
				<p className="text-sm text-[var(--ucm-text-medium)]">
					{t("phoneVerification.didntReceive")}{" "}
					{canResend ? (
						<button
							onClick={handleResend}
							className="text-[var(--ucm-blue-link)] hover:text-[var(--ucm-blue-link-hover)] hover:underline font-medium"
						>
							{t("phoneVerification.resendCode")}
						</button>
					) : (
						<span className="text-[var(--ucm-text-light)]">
							{t("phoneVerification.resendIn", { seconds: countdown })}
						</span>
					)}
				</p>
			</div>

			<button
				onClick={() => code.length === 6 && handleCodeComplete(code)}
				disabled={code.length !== 6 || isLoading}
				className="w-full h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] disabled:bg-[var(--ucm-text-light)] disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2"
			>
				{isLoading ? t("phoneVerification.verifying") : t("common.continue")}
			</button>
		</AuthContainer>
	);
}
